import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import {
  Alert,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useDocuments from 'hooks/document/useDocuments';
import useGymAthleteRetrieve from 'hooks/gym/useGymAthleteRetrieve';
import SimpleHeader from 'layouts/MobileLayout/components/SimpleHeader';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams, useNavigate, useNavigationType } from 'react-router-dom';
import { DocumentList } from 'types';

import DocumentCard from './components/DocumentCard';
import InvoiceBucket, {
  DEFAULT_VISIBLE_ROWS,
  InvoiceBucketDefinition,
  InvoiceBucketState,
} from './components/InvoiceBucket';
import { clearInvoiceViewState, readInvoiceViewState, saveScrollTop } from './invoiceViewState';

const SCROLL_CONTAINER_ID = 'scrollable-content';

const INVOICE_BUCKETS: InvoiceBucketDefinition[] = [
  {
    key: 'past-due',
    label: 'Past Due',
    statuses: ['PAST DUE', 'FAILED PAST DUE', 'PAYMENT FAILED'],
    ordering: '-dueDate',
    color: 'red',
  },
  { key: 'upcoming', label: 'Upcoming', statuses: ['UPCOMING'], ordering: 'dueDate', color: 'kiloBlue' },
  {
    key: 'refunds',
    label: 'Refunds',
    statuses: ['REFUND PENDING', 'REFUNDED', 'PARTIALLY REFUNDED'],
    ordering: '-dueDate',
    color: 'orange',
  },
  { key: 'paid', label: 'Paid', statuses: ['PAID', 'PENDING'], ordering: '-dueDate', color: 'green' },
  { key: 'void', label: 'Void', statuses: ['VOID'], ordering: '-dueDate', color: 'gray' },
];

const AthleteDocumentsPage = (): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();

  const { session } = useAuth();
  const { data: gymData } = useGymAthleteRetrieve(session?.gym?.gymId);

  const [documents, setDocuments] = useState<DocumentList[]>([]);
  const [bucketStates, setBucketStates] = useState<Record<string, InvoiceBucketState>>({});

  const navigationType = useNavigationType();
  const [restoredView] = useState(() => {
    const snapshot = readInvoiceViewState();
    return navigationType === 'POP' ? snapshot : null;
  });

  const hasRestoredScroll = useRef(false);

  const isSigned = tab === 'signed';
  const isInvoicesTab = tab === 'invoices';

  useEffect(() => {
    if (isInvoicesTab && !restoredView) {
      clearInvoiceViewState();
    }
  }, [isInvoicesTab, restoredView]);

  // Redirect to default tab if tab param is invalid
  useEffect(() => {
    if (!['unsigned', 'signed', 'invoices'].includes(tab || '')) {
      navigate('/documents/unsigned', { replace: true });
    }
  }, [tab, navigate]);

  const { data, isLoading, isSuccess, fetchNextPage, hasNextPage, isError } = useDocuments({
    athleteId: session?.athlete?.athleteId,
    isSigned,
    isTemplate: false,
    enabled: !isInvoicesTab,
  });

  useEffect(() => {
    if (isSuccess && data?.pages) {
      setDocuments(data.pages.map(page => page.data.results).flat());
    }
  }, [data?.pages, isSuccess]);

  const handleBucketState = useCallback((key: string, state: InvoiceBucketState) => {
    setBucketStates(previous => {
      const current = previous[key];
      if (
        current &&
        current.count === state.count &&
        current.isLoading === state.isLoading &&
        current.isError === state.isError
      ) {
        return previous;
      }
      return { ...previous, [key]: state };
    });
  }, []);

  const invoicesEnabled = !!gymData?.data.athleteAppAllowInvoiceAccess;
  const invoicesAreLoading =
    invoicesEnabled &&
    (Object.keys(bucketStates).length < INVOICE_BUCKETS.length ||
      INVOICE_BUCKETS.some(bucket => bucketStates[bucket.key]?.isLoading));

  const invoicesIsError = INVOICE_BUCKETS.some(bucket => bucketStates[bucket.key]?.isError);

  useEffect(() => {
    if (!isInvoicesTab) {
      return undefined;
    }
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    if (!container) {
      return undefined;
    }
    let frame: number | null = null;
    const handleScroll = (): void => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        saveScrollTop(container.scrollTop);
      });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isInvoicesTab]);

  useEffect(() => {
    if (!isInvoicesTab || !restoredView || hasRestoredScroll.current) {
      return;
    }
    const stillLoading =
      Object.keys(bucketStates).length < INVOICE_BUCKETS.length ||
      INVOICE_BUCKETS.some(bucket => bucketStates[bucket.key]?.isLoading);
    if (stillLoading) {
      return;
    }
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    if (container) {
      container.scrollTop = restoredView.scrollTop;
    }
    hasRestoredScroll.current = true;
  }, [isInvoicesTab, restoredView, bucketStates]);

  const totalInvoices = useMemo(
    () => INVOICE_BUCKETS.reduce((sum, bucket) => sum + (bucketStates[bucket.key]?.count ?? 0), 0),
    [bucketStates],
  );

  const segmentedControlData = useMemo(() => {
    const segControlData = [
      { label: 'Not signed', value: 'unsigned' },
      { label: 'Signed', value: 'signed' },
      ...(gymData?.data.athleteAppAllowInvoiceAccess ? [{ label: 'Invoices', value: 'invoices' }] : []),
    ];
    return segControlData;
  }, [gymData]);

  return (
    <>
      <SimpleHeader title='Documents' />

      <Stack className='content' id='scrollable-content'>
        <Group position='center'>
          <SegmentedControl
            size='md'
            value={tab}
            onChange={value => navigate(`/documents/${value}`)}
            data={segmentedControlData}
          />
        </Group>

        {isLoading && (
          <Group grow px='md'>
            <Skeleton height={100} />
          </Group>
        )}
        {!isLoading && documents.length === 0 && !isInvoicesTab && (
          <Center>
            <Text color='dimmed'>
              {isSigned ? 'You have no signed documents.' : 'You have no documents to sign.'}
            </Text>
          </Center>
        )}

        {isInvoicesTab && invoicesAreLoading && (
          <Group grow px='md'>
            <Skeleton height={100} />
          </Group>
        )}

        {!invoicesAreLoading && !invoicesIsError && totalInvoices === 0 && isInvoicesTab && (
          <Center>
            <Text color='dimmed'>{"You don't have any invoices yet."}</Text>
          </Center>
        )}

        {(isError || invoicesIsError) && (
          <Center sx={{ flexGrow: 1 }} p='lg'>
            <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
              Something went wrong while fetching data!
            </Alert>
          </Center>
        )}

        {!isInvoicesTab && (
          <InfiniteScroll
            dataLength={documents.length}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <Center py='sm'>
                <Loader />
              </Center>
            }
            scrollableTarget='scrollable-content'
          >
            {isSuccess && documents.length > 0 && (
              <Stack spacing='sm' pb='xl' px='md'>
                {documents.map(document => (
                  <DocumentCard key={document.documentId} tab={tab || 'unsigned'} document={document} />
                ))}
              </Stack>
            )}
          </InfiniteScroll>
        )}

        {isInvoicesTab && gymData?.data.athleteAppAllowInvoiceAccess && (
          <Stack spacing='lg' px='md' pb='xl'>
            {INVOICE_BUCKETS.map(bucket => (
              <InvoiceBucket
                key={bucket.key}
                bucket={bucket}
                athleteId={session?.athlete?.athleteId}
                enabled={isInvoicesTab && !!gymData?.data.athleteAppAllowInvoiceAccess}
                initialVisibleRows={restoredView?.visible[bucket.key] ?? DEFAULT_VISIBLE_ROWS}
                onStateChange={handleBucketState}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default AthleteDocumentsPage;
